/**
 * Address results in global search (RUT-4447).
 *
 * Search used to DROP every Mapbox address that matched a customer in the same
 * results. Typing a customer's exact address therefore removed that very house
 * and left only Mapbox's fuzzy out-of-area matches (Derek: "4606 Rasco Avenue,
 * Odessa, Texas" for an Edina house). Now the address is kept and tagged with
 * the customer living there, so the office can start a new owner from search,
 * and results are re-ranked: exact house number + street first, then nearest
 * to the org's service region.
 */

export interface RankableAddress {
  id: string;
  place_name: string;
  /** [lng, lat] */
  center: [number, number];
  streetAddress: string;
}

export interface AddressCustomerCandidate {
  id: string;
  address?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface ExistingCustomerAtAddress {
  id: string;
  name: string;
}

/** Lowercase, drop punctuation, and fold common street words to one spelling. */
export function normalizeAddress(addr: string): string {
  return addr
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/,/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\b(street|st)\b/g, 'st')
    .replace(/\b(avenue|ave)\b/g, 'ave')
    .replace(/\b(road|rd)\b/g, 'rd')
    .replace(/\b(drive|dr)\b/g, 'dr')
    .replace(/\b(lane|ln)\b/g, 'ln')
    .replace(/\b(boulevard|blvd)\b/g, 'blvd')
    .replace(/\b(circle|cir)\b/g, 'cir')
    .replace(/\b(court|ct)\b/g, 'ct')
    .replace(/\b(north|n)\b/g, 'n')
    .replace(/\b(south|s)\b/g, 's')
    .replace(/\b(east|e)\b/g, 'e')
    .replace(/\b(west|w)\b/g, 'w')
    .trim();
}

/** "4606 Casco Ave, Edina…" → { number: '4606', street: 'casco ave' } on the first comma part. */
function houseNumberAndStreet(addr: string): { number: string; street: string } | null {
  const first = normalizeAddress(addr.split(',')[0] ?? '');
  const m = first.match(/^(\d+[a-z]?)\s+(.+)$/);
  return m ? { number: m[1], street: m[2].trim() } : null;
}

/** The 5-digit ZIP, else the city (second comma part), for telling same-street homes in different towns apart. */
function locality(addr: string): { zip?: string; city?: string } {
  const zip = addr.match(/\b(\d{5})(?:-\d{4})?\b(?![\s\S]*\b\d{5}\b)/)?.[1];
  const city = normalizeAddress(addr.split(',')[1] ?? '') || undefined;
  return { zip, city };
}

/** Same town: ZIPs agree when both have one, else cities agree when both have one. */
function sameLocality(a: string, b: string): boolean {
  const x = locality(a);
  const y = locality(b);
  if (x.zip && y.zip) return x.zip === y.zip;
  if (x.city && y.city) return x.city === y.city;
  return true;
}

function customerName(c: AddressCustomerCandidate): string {
  const display = c.displayName?.trim();
  if (display) return display;
  const full = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  return full || 'this customer';
}

/**
 * The customer already on file at this address, if any. Requires the same house
 * number, street and town, so "4606 Casco Ave" never matches "4606 Rasco Ave",
 * "4608 Casco Ave", or the same street number in another state.
 */
export function customerAtAddress(
  address: RankableAddress,
  customers: readonly AddressCustomerCandidate[],
): ExistingCustomerAtAddress | undefined {
  const target = houseNumberAndStreet(address.place_name);
  if (!target) return undefined;
  for (const c of customers) {
    if (!c.address) continue;
    const own = houseNumberAndStreet(c.address);
    if (
      own &&
      own.number === target.number &&
      own.street === target.street &&
      sameLocality(address.place_name, c.address)
    ) {
      return { id: c.id, name: customerName(c) };
    }
  }
  return undefined;
}

function distanceSq(a: [number, number], b: { lng: number; lat: number }): number {
  const dLng = a[0] - b.lng;
  const dLat = a[1] - b.lat;
  return dLng * dLng + dLat * dLat;
}

/**
 * Order address suggestions for the office: an exact house number + street
 * match for what was typed comes first, then the result nearest the org's
 * service region. Mapbox's own order breaks remaining ties. Pure: returns a new
 * array.
 */
export function rankAddressSuggestions<T extends RankableAddress>(
  addresses: readonly T[],
  query: string,
  regionCenter?: { lng: number; lat: number } | null,
): T[] {
  const typed = houseNumberAndStreet(query);
  const exact = (a: T) => {
    if (!typed) return false;
    const own = houseNumberAndStreet(a.place_name);
    return !!own && own.number === typed.number && own.street.startsWith(typed.street.split(' ')[0]);
  };
  return addresses
    .map((a, i) => ({ a, i, exact: exact(a), d: regionCenter ? distanceSq(a.center, regionCenter) : 0 }))
    .sort((x, y) => {
      if (x.exact !== y.exact) return x.exact ? -1 : 1;
      if (x.d !== y.d) return x.d - y.d;
      return x.i - y.i;
    })
    .map((x) => x.a);
}

/** Keep every address, tag the ones a customer already lives at, and rank. */
export function prepareAddressResults<T extends RankableAddress>(
  addresses: readonly T[],
  customers: readonly AddressCustomerCandidate[],
  query: string,
  regionCenter?: { lng: number; lat: number } | null,
): Array<T & { existingCustomer?: ExistingCustomerAtAddress }> {
  return rankAddressSuggestions(addresses, query, regionCenter).map((a) => {
    const existingCustomer = customerAtAddress(a, customers);
    return existingCustomer ? { ...a, existingCustomer } : { ...a };
  });
}
